import React, { useCallback, useEffect, useState } from "react"
import { FormattedMessage } from "react-intl"
import { Link } from "@carrotfertility/carotene-core"
import { reportError } from "#/utils/ErrorReporting"
import useCreateAirwallexPanToken from "./hooks/useCreateAirwallexPanToken"
import { AirwallexPinDisplay } from "./AirwallexPinDisplay"
import { AirwallexPinRow, AirwallexPinUnavailable, AirwallexPinValueLoading } from "./AirwallexPinRow"
import { AirwallexSca } from "./AirwallexSca"

interface AirwallexPinDisplaySectionProps {
  scaToken: string
}

export const AirwallexPinDisplaySection: React.FC<AirwallexPinDisplaySectionProps> = ({ scaToken }) => {
  const [open, setOpen] = useState(false)
  const [failed, setFailed] = useState(false)

  const {
    mutate: createPanToken,
    data: pantokenResponse,
    isLoading: pantokenIsLoading,
    isError: isErrorGettingPantoken,
    error: pantokenError
  } = useCreateAirwallexPanToken()

  const handleError = useCallback((error: unknown) => {
    reportError(error)
    setFailed(true)
  }, [])

  useEffect(() => {
    if (isErrorGettingPantoken) {
      handleError(new Error(`Error getting pan token for PIN: ${JSON.stringify(pantokenError, null, 2)}`))
    }
  }, [handleError, isErrorGettingPantoken, pantokenError])

  // A token-less response carrying an SCA session code means Airwallex wants the member to
  // re-verify, which is handled below. A token-less response with no session code is broken.
  useEffect(() => {
    if (
      open &&
      !pantokenIsLoading &&
      pantokenResponse &&
      !pantokenResponse.token &&
      !pantokenResponse.sca_session_code
    ) {
      handleError(new Error("Airwallex returned neither a PAN token nor an SCA session code for PIN display"))
    }
  }, [open, handleError, pantokenIsLoading, pantokenResponse])

  useEffect(() => {
    if (open) {
      createPanToken(scaToken)
    }
  }, [open, scaToken, createPanToken])

  if (failed) {
    return (
      <AirwallexPinRow>
        <AirwallexPinUnavailable />
      </AirwallexPinRow>
    )
  }

  if (!open) {
    return (
      <AirwallexPinRow>
        <Link component="button" variant="body2" onClick={() => setOpen(true)}>
          <FormattedMessage defaultMessage="View PIN" description="Button label to reveal a member's Carrot Card PIN" />
        </Link>
      </AirwallexPinRow>
    )
  }

  if (pantokenIsLoading || !pantokenResponse) {
    return (
      <AirwallexPinRow>
        <AirwallexPinValueLoading />
      </AirwallexPinRow>
    )
  }

  // status_code SCA_TOKEN_REQUIRED — the SCA grant that unlocked the card details does not carry
  // over to a second mint, so the member re-verifies before the PIN frame can load.
  if (!pantokenResponse.token) {
    return (
      <>
        <AirwallexPinRow>
          <AirwallexPinValueLoading />
        </AirwallexPinRow>
        <AirwallexSca
          onClose={() => setOpen(false)}
          onError={handleError}
          scaSessionCode={pantokenResponse.sca_session_code}
        />
      </>
    )
  }

  return (
    <AirwallexPinRow>
      <AirwallexPinDisplay onError={handleError} panToken={pantokenResponse} />
    </AirwallexPinRow>
  )
}
